import React from "react";
import Reveal from "../components/Reveal";
import { useAuth } from "../context/AuthContext";
import { SHOP, waLink, dialLink, formatHours } from "../lib/shop";
import { MapPin, Phone, MessageCircle, Clock } from "lucide-react";

const FALLBACK_HOURS = [
  ["Monday", "9:00 – 18:00 · Appointment only"],
  ["Tuesday", "9:00 – 18:00"],
  ["Wednesday", "9:00 – 18:00 · Appointment only"],
  ["Thursday", "9:00 – 18:00 · Appointment only"],
  ["Friday", "9:00 – 18:00"],
  ["Saturday", "9:00 – 17:00 · Appointment only"],
  ["Sunday", "Closed"],
];

export default function Contact() {
  const { shop, settings } = useAuth();
  const s = { ...SHOP, ...(shop || {}) };
  const callLink = dialLink(s.phone);
  const whatsappLink = waLink(s.whatsapp || s.phone, "Hi Who's Next, I'd like to enquire about an appointment.");
  const mapEmbed = s.map_embed || SHOP.mapEmbed;
  const mapLink = s.map_link || SHOP.mapLink;
  const hours = formatHours(settings?.working_hours) || FALLBACK_HOURS;

  return (
    <div data-testid="contact-page" className="pt-32 pb-24 max-w-7xl mx-auto px-5 sm:px-8 min-h-screen">
      <Reveal>
        <div className="label-caps mb-3">Get in touch</div>
        <h1 className="font-display text-6xl sm:text-7xl tracking-tighter mb-4">Visit the Shop</h1>
      </Reveal>

      <div className="mt-14 grid grid-cols-1 lg:grid-cols-2 gap-10">
        <Reveal>
          <div className="space-y-4">
            <a href={callLink} data-testid="contact-call" className="flex items-center gap-4 bg-[#141414] border border-white/5 rounded-2xl p-6 hover:border-[#D4AF37]/30 transition-colors duration-300">
              <span className="w-12 h-12 rounded-full bg-[#D4AF37]/10 flex items-center justify-center"><Phone className="w-5 h-5 text-[#D4AF37]" /></span>
              <div><div className="text-gray-400 text-sm">Call us</div><div className="text-lg">{s.phone}</div></div>
            </a>
            <a href={whatsappLink} target="_blank" rel="noreferrer" data-testid="contact-whatsapp" className="flex items-center gap-4 bg-[#141414] border border-white/5 rounded-2xl p-6 hover:border-[#D4AF37]/30 transition-colors duration-300">
              <span className="w-12 h-12 rounded-full bg-[#25D366]/10 flex items-center justify-center"><MessageCircle className="w-5 h-5 text-[#25D366]" /></span>
              <div><div className="text-gray-400 text-sm">WhatsApp</div><div className="text-lg">Message us instantly</div></div>
            </a>
            <a href={mapLink} target="_blank" rel="noreferrer" data-testid="contact-map-link" className="flex items-center gap-4 bg-[#141414] border border-white/5 rounded-2xl p-6 hover:border-[#D4AF37]/30 transition-colors duration-300">
              <span className="w-12 h-12 rounded-full bg-[#D4AF37]/10 flex items-center justify-center"><MapPin className="w-5 h-5 text-[#D4AF37]" /></span>
              <div><div className="text-gray-400 text-sm">Find us</div><div className="text-lg">{s.address}</div></div>
            </a>

            <div className="bg-[#141414] border border-white/5 rounded-2xl p-6">
              <div className="flex items-center gap-2 mb-4 label-caps"><Clock className="w-4 h-4" /> Opening Hours</div>
              <ul className="space-y-2" data-testid="contact-hours">
                {hours.map(([d, h]) => (
                  <li key={d} className="flex justify-between text-sm border-b border-white/5 pb-2">
                    <span className="text-gray-300">{d}</span>
                    <span className={h === "Closed" ? "text-gray-600" : "text-gray-400"}>{h}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </Reveal>

        <Reveal delay={0.1}>
          <div className="rounded-2xl overflow-hidden border border-white/10 h-full min-h-[500px]">
            <iframe title="Google Map" src={mapEmbed} className="w-full h-full" loading="lazy" />
          </div>
        </Reveal>
      </div>
    </div>
  );
}
